import { transparentize } from 'polished'
import React, { useContext, useMemo } from 'react'
import styled, {
  ThemeProvider as StyledComponentsThemeProvider,
  ThemeContext,
  createGlobalStyle,
  css,
  DefaultTheme
} from 'styled-components'
import { useIsDarkMode } from '../state/user/hooks'
import { Text, TextProps } from '../components/Text'
import { Colors } from './styled'

export * from './components'

// Re-export ThemeContext for use in components
export { ThemeContext }

/**
 * Custom useTheme hook that guarantees a non-null theme.
 * Use this instead of useContext(ThemeContext) to avoid undefined checks.
 */
export function useTheme(): DefaultTheme {
  const theme = useContext(ThemeContext)
  if (!theme) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return theme
}

const MEDIA_WIDTHS = {
  upToExtraSmall: 500,
  upToSmall: 600,
  upToMedium: 960,
  upToLarge: 1280
}

const mediaWidthTemplates: { [width in keyof typeof MEDIA_WIDTHS]: typeof css } = Object.keys(MEDIA_WIDTHS).reduce(
  (accumulator, size) => {
    ;(accumulator as any)[size] = (a: any, b: any, c: any) => css`
      @media (max-width: ${(MEDIA_WIDTHS as any)[size]}px) {
        ${css(a, b, c)}
      }
    `
    return accumulator
  },
  {}
) as any

const white = '#FFFFFF'
const black = '#000000'

// old input
// const white = '#FFFFFF'
// const black = '#000000'

export function colors(darkMode: boolean): Colors {
  return {
    // base
    white,
    black,

    // text
    text1: darkMode ? '#FFFFFF' : '#131313',
    text2: darkMode ? '#C3C5CB' : '#565A69',
    text3: darkMode ? '#6C7284' : '#888D9B',
    text4: darkMode ? '#565A69' : '#C3C5CB',
    text5: darkMode ? '#2C2F36' : '#EDEEF2',

    // old text
    // text1: darkMode ? '#FFFFFF' : '#000000',
    // text2: darkMode ? '#C3C5CB' : '#565A69',
    // text3: darkMode ? '#6C7284' : '#888D9B',
    // text4: darkMode ? '#565A69' : '#C3C5CB',
    // text5: darkMode ? '#2C2F36' : '#EDEEF2',
    
    // backgrounds / greys
    bg1: darkMode ? '#131313' : '#FFFFFF',
    bg2: darkMode ? '#2C2F36' : '#F7F8FA',
    bg3: darkMode ? '#40444F' : '#EDEEF2',
    bg4: darkMode ? '#565A69' : '#CED0D9',
    bg5: darkMode ? '#6C7284' : '#888D9B',
    
    // old backgrounds / greys
    // bg1: darkMode ? '#212429' : '#FFFFFF',
    // bg2: darkMode ? '#2C2F36' : '#F7F8FA',
    // bg3: darkMode ? '#40444F' : '#EDEEF2',
    // bg4: darkMode ? '#565A69' : '#CED0D9',
    // bg5: darkMode ? '#6C7284' : '#888D9B',

    // specialty colors
    modalBG: darkMode ? 'rgba(0,0,0,.425)' : 'rgba(0,0,0,0.3)',
    advancedBG: darkMode ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.6)',
    
    // old specialty colors
    // modalBG: darkMode ? 'rgba(0,0,0,.425)' : 'rgba(0,0,0,0.3)',
    // advancedBG: darkMode ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.6)',

    // primary colors - green shades
    primary1: darkMode ? '#33FF99' : '#33FF99',
    primary2: darkMode ? '#00F77C' : '#5AFFAD',
    primary3: darkMode ? '#00BC5E' : '#81FFC0',
    primary4: darkMode ? '#00954B' : '#A9FFD4',
    primary5: darkMode ? '#006E37' : '#E4FFF1',
    // old primary colors - blue shades
    // primary1: darkMode ? '#034c05' : '#034c05',
    // primary2: darkMode ? '#0c8412' : '#0c8412',
    // primary3: darkMode ? '#3cba3c' : '#3cba3c',
    // primary4: darkMode ? '#376bad' : '#F6DDE8',
    // primary5: darkMode ? '#153d6f' : '#FDEAF1',

    // color text - green, black, white
    primaryText1: darkMode ? '#33FF99' : '#33FF99',
    primaryText2: darkMode ? '#131313' : '#131313',
    primaryText3: darkMode ? '#FFFFFF' : '#FFFFFF',
    primaryText4: darkMode ? '#33FF99' : '#131313',
    primaryText5: darkMode ? '#FFFFFF' : '#33FF99',
    primaryText6: darkMode ? '#131313' : '#FFFFFF',
    primaryText7: darkMode ? '#FFFFFF' : '#131313',

    // old color text - blue
    // primaryText1: darkMode ? '#0c8412' : '#0c8412',

    // secondary colors - green alternatives
    secondary1: darkMode ? '#33FF88' : '#33FFAA',
    secondary2: darkMode ? '#33FF77' : '#33FFBB',
    secondary3: darkMode ? '#33FF66' : '#33FFCC',
    
    // old secondary colors - pinks
    // secondary1: darkMode ? '#0c8412' : '#0c8412',
    // secondary2: darkMode ? '#17000b26' : '#F6DDE8',
    // secondary3: darkMode ? '#17000b26' : '#FDEAF1',

    // other
    black1: '#131313',
    blue1: '#33FFF4',
    green1: '#33FF99',
    orange1: '#F3841E',
    red1: '#FD025D',
    white1: '#FFFFFF',
    yellow1: '#FFED00',
    
    // old other
    // red1: '#FF6871',
    // red2: '#F82D3A',
    // green1: '#27AE60',
    // yellow1: '#FFE270',
    // yellow2: '#F3841E'

    // dont wanna forget these blue yet
    // blue4: darkMode ? '#153d6f' : '#C4D9F8',
    // blue5: darkMode ? '#153d6f' : '#EBF4FF',
  }
}

export function theme(darkMode: boolean): DefaultTheme {
  return {
    ...colors(darkMode),

    grids: {
      sm: 8,
      md: 12,
      lg: 24
    },

    //shadows
    shadow1: darkMode ? '#000' : '#2F80ED',

    // media queries
    mediaWidth: mediaWidthTemplates,

    // css snippets
    flexColumnNoWrap: css`
      display: flex;
      flex-flow: column nowrap;
    `,
    flexRowNoWrap: css`
      display: flex;
      flex-flow: row nowrap;
    `
  }
}

export default function ThemeProvider({ children }: { children: React.ReactNode }) {
  const darkMode = useIsDarkMode()

  const themeObject = useMemo(() => theme(darkMode), [darkMode])

  return <StyledComponentsThemeProvider theme={themeObject}>{children}</StyledComponentsThemeProvider>
}

const TextWrapper = styled(Text)<{ $color?: keyof Colors }>`
  color: ${({ $color, theme }) => ($color ? (theme as any)[$color] : undefined)};
`

export const TYPE = {
  main(props: TextProps) {
    return <TextWrapper fontWeight={500} $color={'text2'} {...props} />
  },
  link(props: TextProps) {
    return <TextWrapper fontWeight={500} $color={'primary1'} {...props} />
  },
  black(props: TextProps) {
    return <TextWrapper fontWeight={500} $color={'text1'} {...props} />
  },
  body(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={16} $color={'text1'} {...props} />
  },
  largeHeader(props: TextProps) {
    return <TextWrapper fontWeight={600} fontSize={24} {...props} />
  },
  mediumHeader(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={20} {...props} />
  },
  subHeader(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={14} {...props} />
  },
  red(props: TextProps) {
    return <TextWrapper fontWeight={500} $color={'red1'} {...props} />
  },
  green(props: TextProps) {
    return <TextWrapper fontWeight={500} $color={'green1'} {...props} />
  },
  blue(props: TextProps) {
    return <TextWrapper fontWeight={500} $color={'blue1'} {...props} />
  },
  yellow(props: TextProps) {
    return <TextWrapper fontWeight={500} $color={'yellow1'} {...props} />
  },
  darkGray(props: TextProps) {
    return <TextWrapper fontWeight={500} $color={'text3'} {...props} />
  },
  gray(props: TextProps) {
    return <TextWrapper fontWeight={500} $color={'bg3'} {...props} />
  },
  italic(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={12} fontStyle={'italic'} $color={'text2'} {...props} />
  },
  error({ error, ...props }: { error: boolean } & TextProps) {
    return <TextWrapper fontWeight={500} $color={error ? 'red1' : 'text2'} {...props} />
  }
}

export const FixedGlobalStyle = createGlobalStyle`
html, input, textarea, button {
  font-family: 'DM Sans', sans-serif;
  letter-spacing: -0.018em;
  font-display: fallback;
}
@supports (font-variation-settings: normal) {
  html, input, textarea, button {
    font-family: 'DM Sans', sans-serif;
  }
}

html,
body {
  margin: 0;
  padding: 0;
}

* {
  box-sizing: border-box;
}

button {
  user-select: none;
}

html {
  font-size: 16px;
  font-variant: none;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}
`

// Swap Table BG Color
export const ThemedGlobalStyle = createGlobalStyle`
html {
  color: ${({ theme }) => theme.text1};
  background-color: ${({ theme }) => theme.bg2};
}

body {
  min-height: 100vh;
  background-position: 0 -30vh;
  background-repeat: no-repeat;
  background-image: ${({ theme }) =>
    `radial-gradient(50% 50% at 50% 50%, ${transparentize(0.9, theme.primary1)} 0%, ${transparentize(
      1,
      theme.bg1
    )} 100%)`};
}

/* ============================================
   ETCswap Global Button & Component Styles
   ============================================ */

/* Base button improvements */
button {
  font-family: inherit;
  font-weight: 500;
  border-radius: 12px;
  transition: all 0.15s ease;
}

/* Ensure all interactive elements have smooth transitions */
a, button, input, select, textarea {
  transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;
}

/* Subtle hover lift for buttons */
button:not([disabled]):hover {
  transform: translateY(-1px);
}

button:not([disabled]):active {
  transform: translateY(0);
}

/* Improve focus states */
button:focus-visible {
  outline: 2px solid ${({ theme }) => theme.primary1};
  outline-offset: 2px;
}

/* Links within buttons should inherit color */
button a {
  color: inherit;
  text-decoration: none;
}

/* ============================================
   RainbowKit / Reown Overrides
   Style third-party wallet components to match ETCswap theme
   ============================================ */

/* RainbowKit Connect Button - primary action */
[data-rk] button,
.iekbcc0 button {
  font-family: 'DM Sans', sans-serif !important;
  font-weight: 600 !important;
  border-radius: 12px !important;
}

/* Primary/Connect buttons - green background, dark text */
[data-rk] button[data-testid="rk-connect-button"],
[data-rk] button[class*="connect"],
.iekbcc0 button {
  background-color: ${({ theme }) => theme.green1} !important;
  color: ${({ theme }) => theme.black1} !important;
  border: none !important;
  padding: 10px 16px !important;
  font-size: 14px !important;
}

[data-rk] button[data-testid="rk-connect-button"]:hover,
[data-rk] button[class*="connect"]:hover,
.iekbcc0 button:hover {
  background-color: ${({ theme }) => theme.primary2} !important;
  transform: translateY(-1px);
}

/* RainbowKit modal styling */
[data-rk] [role="dialog"],
[data-rk] [class*="modal"] {
  background-color: ${({ theme }) => theme.bg1} !important;
  border: 1px solid ${({ theme }) => theme.bg3} !important;
  border-radius: 20px !important;
}

/* RainbowKit wallet options */
[data-rk] button[data-testid*="wallet-option"] {
  background-color: ${({ theme }) => theme.bg2} !important;
  border: 1px solid ${({ theme }) => theme.bg3} !important;
  color: ${({ theme }) => theme.text1} !important;
  border-radius: 12px !important;
}

[data-rk] button[data-testid*="wallet-option"]:hover {
  background-color: ${({ theme }) => theme.bg3} !important;
  border-color: ${({ theme }) => theme.primary1} !important;
}

/* ============================================
   Reown AppKit / Web3Modal Overrides
   ============================================ */

/* Target Reown/AppKit components */
w3m-modal,
w3m-button,
wui-flex,
wui-card {
  --wui-color-accent-100: ${({ theme }) => theme.green1} !important;
  --wui-color-fg-100: ${({ theme }) => theme.text1} !important;
  --wui-color-bg-100: ${({ theme }) => theme.bg1} !important;
  --wui-color-bg-125: ${({ theme }) => theme.bg2} !important;
  --wui-color-bg-150: ${({ theme }) => theme.bg3} !important;
  --w3m-accent: ${({ theme }) => theme.green1} !important;
  --w3m-color-mix: ${({ theme }) => theme.bg1} !important;
}

/* Reown connect button */
w3m-button button,
w3m-connect-button button,
appkit-button button {
  background-color: ${({ theme }) => theme.green1} !important;
  color: ${({ theme }) => theme.black1} !important;
  border-radius: 12px !important;
  font-family: 'DM Sans', sans-serif !important;
  font-weight: 600 !important;
}

/* Account button when connected */
w3m-account-button button {
  background-color: ${({ theme }) => theme.bg2} !important;
  border: 1px solid ${({ theme }) => theme.bg3} !important;
  color: ${({ theme }) => theme.text1} !important;
}

/* ============================================
   Global Green Button Text Fix
   ============================================ */

/*
 * CRITICAL: Ensure ALL buttons with green backgrounds have dark text.
 * styled-components generates hashed class names, so we can't rely on class selectors.
 * Instead, we target by ID and use a data attribute for green buttons.
 */

/* Target known button IDs and ensure dark text on green backgrounds */
#confirm-swap-or-send,
#confirm-swap-or-send > *,
#confirm-swap-or-send span,
#confirm-swap-or-send div,
#swap-button,
#swap-button > *,
#swap-button span,
#swap-button div,
#join-pool-button,
#join-pool-button > *,
#confirm-supply,
#confirm-supply > *,
button[id*="confirm"],
button[id*="confirm"] > *,
button[id*="confirm"] span,
button[id*="confirm"] div {
  color: ${({ theme }) => theme.black1} !important;
}

/* Data attribute approach for green buttons - components should add data-variant="primary" */
button[data-variant="primary"],
button[data-variant="primary"] > *,
button[data-variant="primary"] span,
button[data-variant="primary"] div {
  color: ${({ theme }) => theme.black1} !important;
}

/* ============================================
   Hide Injected Wallet Branding
   Reown/RainbowKit inject wallet icons that break layout
   ============================================ */

/* Hide all injected wallet avatars and icons */
#web3-status-connected img,
#web3-status-connected wui-avatar,
#web3-status-connected w3m-avatar,
#web3-status-connected [class*="avatar"],
#web3-status-connected [class*="Avatar"],
#connect-wallet img:not([alt]),
button[id*="web3"] > img,
button[id*="web3"] wui-avatar,
button[id*="web3"] w3m-avatar {
  display: none !important;
}

/* Ensure wallet button content stays in a row */
#web3-status-connected,
#web3-status-connected button,
#web3-status-connected > *,
#web3-status-connected [data-rk],
#web3-status-connected [data-rk] button {
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center !important;
  flex-wrap: nowrap !important;
  background-color: ${({ theme }) => theme.bg1} !important;
  background: ${({ theme }) => theme.bg1} !important;
  border: 1px solid ${({ theme }) => theme.bg3} !important;
  color: ${({ theme }) => theme.text1} !important;
}

#web3-status-connected:hover,
#web3-status-connected:focus,
#web3-status-connected button:hover,
#web3-status-connected button:focus,
#web3-status-connected [data-rk] button:hover,
#web3-status-connected [data-rk] button:focus {
  background-color: ${({ theme }) => theme.bg1} !important;
  background: ${({ theme }) => theme.bg1} !important;
  border-color: ${({ theme }) => theme.green1} !important;
  transform: none !important;
}

/* ============================================
   Scrollbar Styling
   ============================================ */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: ${({ theme }) => theme.bg1};
}

::-webkit-scrollbar-thumb {
  background: ${({ theme }) => theme.bg3};
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: ${({ theme }) => theme.bg4};
}
`
